/* =============================================================================
   SafeWake ONE · assets/money.js — THE single money formatter (v1.0 · 2026-09-05)
   -----------------------------------------------------------------------------
   Every JS-side price in this theme goes through SWMoney.formatMoney(cents, currency).
   The currency comes from window.SWMarket (cartCurrency → currency; both set by
   Liquid: snippets/money-context.liquid + snippets/country-context.liquid) or from
   the `currency` field of a /cart.js response — never from a hardcoded string.

   WHAT "CENTS" MEANS — verified by real run, 2026-09-05, on the six live SafeWake
   storefronts (safewakekw.com, safewakeuae.com, safewakebh.com, safewakeom.com,
   safewakeksa.com, safewakeqa.com):
     Shopify hands JS an INTEGER = price × 100 for EVERY currency, including the
     3-decimal dinars/rial. It is NOT the ISO minor unit (fils = 1/1000).
       KW  products.json "21.900" · /products/x.js 2190 · {{ product | json }} 2190 · {{ 2190 | money }} → "21.900 KD"
       BH  "25.900" ↔ 2590 → "25.900 BD"        OM  "26.900" ↔ 2690 → "26,900 OMR"
       AE  "171.68" ↔ 17168 → "Dhs. 171.68"     SA  "503.79" ↔ 50379 → "503.79 SR"
       QA  "248.99" ↔ 24899 → "QAR 248,99"
     Therefore KWD 0.250 = 25 · BHD 49.500 = 4950 · OMR 12.345 = 1234.5 (fractional
     cents are legal input and are formatted exactly, no float drift).
     ⛔ Never divide by 1000. Iron Rule 18 is right on this; W06-S3 §"storage
        convention" is wrong. Tabby's own Shopify snippet also does variant.price / 100.

   NO HARDCODED DIVISOR — the live page calibrates from Shopify's own money filter:
   snippets/money-context.liquid renders {{ 123456789 | money }} and money.js derives
   cents-per-unit, decimal count, separators and the symbol template from that string.
   If Shopify ever changed the integer convention for a presentment currency, the
   formatter would follow automatically. The tables below are the OFFLINE fallback
   (node tests, pages without the probe).

   API (all currency args optional → active market currency):
     formatMoney(cents, code, {format, group})  "1,137.95 AED" / calibrated "Dhs. 1,137.95"
     formatAmount(cents, code)                  "1,137.95"          (no symbol)
     toFixed(cents, code)                       "1137.95" / "21.900" (dot-decimal, no grouping — BNPL SDK input)
     toDecimal(cents, code)                     1137.95             (Number)
     fromDecimal("21.900" | 21.9, code)         2190                (Shopify JSON decimal strings → cents)
     parse("Dhs. 1,137.95", code)               113795              (display string → cents, calibrated separators)
     formatMoneyArabic(cents, code)             "١٬١٣٧.٩٥ د.إ"       (Eastern-Arabic digits + Arabic symbol)
     decimals(code) · centsPerUnit(code) · currency() · country() · calibrate(code, probe) · configure(obj) · reset()
   ============================================================================= */
(function (root) {
  'use strict';

  var FALLBACK_DECIMALS = {
    KWD: 3, BHD: 3, OMR: 3, JOD: 3, IQD: 3, LYD: 3, TND: 3,
    AED: 2, SAR: 2, QAR: 2, USD: 2, EUR: 2, GBP: 2, EGP: 2, INR: 2,
    JPY: 0, KRW: 0
  };
  var DEFAULT_CENTS_PER_UNIT = 100; /* Shopify "cents" = price × 100 (see header) */
  var ARABIC_SYMBOL = { AED: 'د.إ', KWD: 'د.ك', SAR: 'ر.س', BHD: 'د.ب', OMR: 'ر.ع.', QAR: 'ر.ق' };
  var ARABIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

  var calibrated = {}; /* code → { decimals, centsPerUnit, decimalSep, groupSep, template, source } */

  function market() { return (root && root.SWMarket) || {}; }
  function currency() {
    var m = market();
    return String(m.cartCurrency || m.currency || 'AED').toUpperCase();
  }
  function country() { return String(market().country || '').toUpperCase(); }
  function norm(code) { return code ? String(code).toUpperCase() : currency(); }
  function cal(code) { return calibrated[norm(code)] || null; }

  function decimals(code) {
    code = norm(code);
    var c = cal(code), m = market();
    if (c) return c.decimals;
    if (String(m.currency || '').toUpperCase() === code && typeof m.decimals === 'number') return m.decimals;
    return FALLBACK_DECIMALS[code] != null ? FALLBACK_DECIMALS[code] : 2;
  }
  function centsPerUnit(code) { var c = cal(code); return c ? c.centsPerUnit : DEFAULT_CENTS_PER_UNIT; }
  function separators(code) {
    var c = cal(code);
    return c ? { decimal: c.decimalSep, group: c.groupSep } : { decimal: '.', group: ',' };
  }
  function template(code) {
    code = norm(code);
    var c = cal(code);
    return (c && c.template) ? c.template : '{{amount}} ' + code;
  }

  /* cents → integer minor units at the currency's decimal count. Exact: 1234.5 KWD-cents → 12345. */
  function minorUnits(cents, code) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    return Math.round(n * Math.pow(10, decimals(code)) / centsPerUnit(code));
  }
  function group(intStr, sep) { return sep ? intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : intStr; }
  function pad(frac, d) { frac = String(frac); while (frac.length < d) frac = '0' + frac; return frac; }

  function formatAmount(cents, code, opts) {
    code = norm(code); opts = opts || {};
    var d = decimals(code), sep = separators(code);
    var m = minorUnits(cents, code), neg = m < 0;
    m = Math.abs(m);
    var pow = Math.pow(10, d);
    var whole = String(Math.floor(m / pow));
    var out = group(whole, opts.group === false ? '' : sep.group) + (d ? sep.decimal + pad(m % pow, d) : '');
    return (neg ? '-' : '') + out;
  }
  function formatMoney(cents, code, opts) {
    code = norm(code); opts = opts || {};
    var tpl = opts.format || template(code);
    return tpl.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, formatAmount(cents, code, opts));
  }
  function toFixed(cents, code) {
    code = norm(code);
    var d = decimals(code), m = minorUnits(cents, code), neg = m < 0;
    m = Math.abs(m);
    var pow = Math.pow(10, d);
    return (neg ? '-' : '') + Math.floor(m / pow) + (d ? '.' + pad(m % pow, d) : '');
  }
  function toDecimal(cents, code) { var n = Number(cents); return isFinite(n) ? n / centsPerUnit(code) : 0; }
  /* Shopify JSON decimal strings (/products.json "21.900", always dot-decimal) or plain numbers → cents. */
  function fromDecimal(value, code) {
    var n = typeof value === 'number' ? value : parseFloat(String(value == null ? '' : value).replace(/[^\d.\-]/g, ''));
    if (!isFinite(n)) return 0;
    return Math.round(n * centsPerUnit(code) * 1000) / 1000; /* keeps 12.345 OMR → 1234.5 */
  }
  /* Display string → cents using the calibrated separators ("Dhs. 1,137.95" · "26,900 OMR" · "21.900 KD"). */
  function parse(text, code) {
    code = norm(code);
    var sep = separators(code);
    var m = String(text == null ? '' : text).match(/-?\d[\d.,'\s\u00a0\u202f]*/);
    if (!m) return NaN;
    var num = m[0].replace(/[\s\u00a0\u202f']/g, '');
    if (sep.group && sep.group !== ' ') num = num.split(sep.group).join('');
    if (sep.decimal && sep.decimal !== '.') num = num.replace(sep.decimal, '.');
    var n = parseFloat(num);
    return isFinite(n) ? Math.round(n * centsPerUnit(code) * 1000) / 1000 : NaN;
  }

  function toArabicDigits(s) { return String(s == null ? '' : s).replace(/\d/g, function (d) { return ARABIC_DIGITS[+d]; }); }
  function arabicSymbol(code) { code = norm(code); return ARABIC_SYMBOL[code] || code; }
  function formatMoneyArabic(cents, code) { return toArabicDigits(formatAmount(cents, code)) + ' ' + arabicSymbol(code); }

  /* Derive everything from Shopify's own money filter output for a known integer.
     probe = { cents: 123456789, plain: {{ 123456789 | money_without_currency }}, text: {{ 123456789 | money }} }
     With ≥ 7 digits the grouping separator occurs ≥ 2 times and the decimal one exactly once, so they never collide. */
  function calibrate(code, probe) {
    code = norm(code);
    if (!probe || (probe.plain == null && probe.text == null)) return null;
    var text = probe.text == null ? '' : String(probe.text);
    var plain = probe.plain == null ? '' : String(probe.plain).trim();
    /* Shopify's "HTML without currency" format can differ from the "HTML with currency" one
       (e.g. "1,234,567.890" vs "1.234.567,890 OMR"). The `money` filter is what customers see,
       so when the two disagree the numeric run inside `text` wins. */
    if (!plain || text.indexOf(plain) < 0) {
      var run = text.match(/-?\d[\d.,'\s  ]*\d|\d/);
      if (run) plain = run[0].trim();
      if (!plain) return null;
    }
    var seps = plain.replace(/\d/g, '');
    var decimalSep = '', groupSep = '', d = 0;
    if (seps.length) {
      var last = seps.charAt(seps.length - 1);
      var occurrences = seps.split(last).length - 1;
      var tail = plain.length - plain.lastIndexOf(last) - 1;
      if (occurrences === 1 && tail >= 1 && tail <= 3) { decimalSep = last; d = tail; }
      var rest = decimalSep ? seps.split(decimalSep).join('') : seps;
      if (rest.length) groupSep = rest.charAt(0);
    }
    var numStr = plain;
    if (groupSep) numStr = numStr.split(groupSep).join('');
    if (decimalSep && decimalSep !== '.') numStr = numStr.replace(decimalSep, '.');
    var value = parseFloat(numStr);
    var cents = Number(probe.cents);
    if (!isFinite(value) || value <= 0 || !isFinite(cents) || cents <= 0) return null;
    var cpu = Math.pow(10, Math.round(Math.log(cents / value) / Math.LN10));
    var tpl = null;
    if (probe.text != null && String(probe.text).indexOf(plain) >= 0) tpl = String(probe.text).replace(plain, '{{amount}}');
    calibrated[code] = { decimals: d, centsPerUnit: cpu, decimalSep: decimalSep || '.', groupSep: groupSep, template: tpl, source: probe.source || 'probe' };
    return calibrated[code];
  }
  function configure(cfg) {
    cfg = cfg || {};
    root.SWMarket = root.SWMarket || {};
    if (cfg.currency) root.SWMarket.currency = String(cfg.currency).toUpperCase();
    if (cfg.cartCurrency) root.SWMarket.cartCurrency = String(cfg.cartCurrency).toUpperCase();
    if (cfg.country) root.SWMarket.country = String(cfg.country).toUpperCase();
    if (typeof cfg.decimals === 'number') root.SWMarket.decimals = cfg.decimals;
    if (cfg.probe) { root.SWMarket.money = { probe: cfg.probe, source: 'configure' }; init(); }
    return SWMoney;
  }
  function reset() {
    calibrated = {};
    SWMoney.calibrationWarning = null;
    if (root.SWMarket) { delete root.SWMarket.cartCurrency; delete root.SWMarket.decimals; delete root.SWMarket.money; }
    return SWMoney;
  }
  /* A probe is only trusted when it was demonstrably rendered in the market currency. Two independent checks:
     (1) probe.withCurrency ({{ n | money_with_currency }}) must contain the ISO code the page declares;
     (2) the decimal count the probe implies must equal SWMarket.decimals when country-context set one.
     Reason: a Liquid context that renders the layout in the store's base currency while sections render the
     market currency (seen on a local harness 2026-09-05) would otherwise calibrate KWD from an AED string and
     silently print 2 dp on a 3-dp currency. On mismatch we keep the ISO table (3 dp for KWD) and record why. */
  function probeMatchesMarket(code, probe) {
    var m = market();
    if (probe.withCurrency != null && String(probe.withCurrency).toUpperCase().indexOf(code) < 0) return 'withCurrency lacks ' + code;
    if (typeof m.decimals === 'number' && String(m.currency || '').toUpperCase() === code) {
      var plain = probe.plain == null ? '' : String(probe.plain).trim();
      var run = plain || ((String(probe.text || '').match(/-?\d[\d.,'\s  ]*\d|\d/) || [''])[0]);
      var seps = run.replace(/\d/g, '');
      var d = 0;
      if (seps.length) {
        var last = seps.charAt(seps.length - 1), occ = seps.split(last).length - 1, tail = run.length - run.lastIndexOf(last) - 1;
        if (occ === 1 && tail >= 1 && tail <= 3) d = tail;
      }
      if (d !== m.decimals) return 'probe implies ' + d + ' dp, market declares ' + m.decimals;
    }
    return null;
  }
  function init() {
    var m = market();
    var p = (m.money && m.money.probe) || m.moneyProbe;
    if (!p) return;
    var code = currency();
    var why = probeMatchesMarket(code, p);
    if (why) {
      SWMoney.calibrationWarning = 'money.js: probe rejected for ' + code + ' (' + why + ') — using ISO table';
      if (root.console && console.warn) console.warn(SWMoney.calibrationWarning, p);
      return;
    }
    calibrate(code, p);
  }

  var SWMoney = {
    formatMoney: formatMoney, formatAmount: formatAmount, toFixed: toFixed, toDecimal: toDecimal,
    fromDecimal: fromDecimal, parse: parse, formatMoneyArabic: formatMoneyArabic, toArabicDigits: toArabicDigits,
    arabicSymbol: arabicSymbol, decimals: decimals, centsPerUnit: centsPerUnit, currency: currency, country: country,
    calibrate: calibrate, configure: configure, reset: reset, init: init, probeMatchesMarket: probeMatchesMarket,
    calibration: function (code) { return cal(code); }, calibrationWarning: null,
    FALLBACK_DECIMALS: FALLBACK_DECIMALS, DEFAULT_CENTS_PER_UNIT: DEFAULT_CENTS_PER_UNIT, version: '1.0.0'
  };
  init();
  root.SWMoney = SWMoney;
  if (typeof module === 'object' && module && module.exports) module.exports = SWMoney;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
